import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Plus,
  Filter,
  Search,
  List,
  Grid,
  Mail,
  Archive,
  Calendar,
  Download,
  Upload,
  ChevronDown,
  Phone,
  MessageSquare,
  FileText,
  MoreVertical, 
  ChevronRight,
  Trash2
} from 'lucide-react';
import { useLocation } from 'react-router-dom';
import EmptyState from '../components/EmptyState';
import CreateBookingModal from '../components/CreateBookingModal';
import BookingInfoModal from '../components/BookingInfoModal';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import PermissionGuard from '../components/PermissionGuard';
import { useBookings } from '../hooks/useBookings'; 
import { deleteBooking, getBooking, getBookings, type Booking } from '../lib/bookings';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabase';
import { triggerBookingAutomations } from '../lib/automationsV2';
import { cancelBookingAutomations } from '../services/automationCancellationService';
import { BookingSkeletonList } from '../components/BookingSkeleton';

const statusOptions = [
  { label: 'All Bookings', value: 'all' },
  { label: 'Scheduled', value: 'scheduled' },
  { label: 'In Progress', value: 'in_progress' },
  { label: 'Completed', value: 'completed' },
  { label: 'Cancelled', value: 'cancelled' },
];

const dateFilterOptions = [
  { label: 'This Month', value: 'this_month' },
  { label: 'Last Month', value: 'last_month' },
  { label: 'All Time', value: 'all_time' },
];

interface BookingsProps {
  /**
   * When rendered inside the calendar's list view, this pins the list to the
   * calendar's current month, hides the status/month/search filters, and pins
   * an "In Progress" section to the top of the list.
   */
  embeddedMonth?: Date;
}

export function Bookings({ embeddedMonth }: BookingsProps = {}) {
  const { selectedBusiness } = useBusiness();
  const location = useLocation();
  const [permissionLevel, setPermissionLevel] = useState<'manage' | 'view' | 'disabled'>('manage');
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [selectedBookings, setSelectedBookings] = useState<Set<string>>(new Set());
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(statusOptions[0]);
  const [isDateFilterOpen, setIsDateFilterOpen] = useState(false);
  const [selectedDateFilter, setSelectedDateFilter] = useState(dateFilterOptions[0]); // Default to "This Month"
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateBookingModalOpen, setIsCreateBookingModalOpen] = useState(false);
  const [isBookingInfoModalOpen, setIsBookingInfoModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  // Calculate date range based on selected filter
  const getDateRange = () => {
    // Embedded in the calendar: lock the list to the calendar's current month.
    if (embeddedMonth) {
      return {
        startDate: new Date(embeddedMonth.getFullYear(), embeddedMonth.getMonth(), 1),
        endDate: new Date(embeddedMonth.getFullYear(), embeddedMonth.getMonth() + 1, 0)
      };
    }
    const now = new Date();
    switch (selectedDateFilter.value) {
      case 'this_month':
        return {
          startDate: new Date(now.getFullYear(), now.getMonth(), 1),
          endDate: new Date(now.getFullYear(), now.getMonth() + 1, 0)
        };
      case 'last_month':
        return {
          startDate: new Date(now.getFullYear(), now.getMonth() - 1, 1),
          endDate: new Date(now.getFullYear(), now.getMonth(), 0)
        };
      case 'all_time':
      default:
        return { startDate: undefined, endDate: undefined };
    }
  };

  const dateRange = getDateRange();
  
  // Use date filtering only when not searching (search should override date filters)
  const bookingOptions = searchQuery.trim() ? undefined : dateRange;
  
  const { bookings, loading, error, totalCount, refetch } = useBookings(selectedBusiness?.id, bookingOptions);
  const [invoiceByBooking, setInvoiceByBooking] = useState<Record<string, string>>({});

  // Embedded view pins every in-progress job (any month) to the top of the list.
  const [inProgressBookings, setInProgressBookings] = useState<Booking[]>([]);
  useEffect(() => {
    if (!embeddedMonth || !selectedBusiness?.id) {
      setInProgressBookings([]);
      return;
    }
    let active = true;
    getBookings(selectedBusiness.id, { status: 'in_progress' })
      .then((data) => {
        if (active) setInProgressBookings(data as Booking[]);
      })
      .catch(() => {
        if (active) setInProgressBookings([]);
      });
    return () => {
      active = false;
    };
    // `bookings` is a dep so the pinned section refreshes whenever the list refetches.
  }, [embeddedMonth, selectedBusiness?.id, bookings]);

  // Map booking_id → invoice number (new invoicing system) for display + search.
  useEffect(() => {
    const ids = bookings.map((b) => b.id).filter(Boolean);
    if (!selectedBusiness?.id || ids.length === 0) { setInvoiceByBooking({}); return; }
    let active = true;
    (async () => {
      const map: Record<string, string> = {};
      for (let i = 0; i < ids.length; i += 200) {
        const { data } = await supabase
          .from('invoices')
          .select('booking_id, invoice_number')
          .eq('business_id', selectedBusiness.id)
          .in('booking_id', ids.slice(i, i + 200));
        for (const r of data || []) {
          if (r.booking_id && r.invoice_number) map[r.booking_id as string] = r.invoice_number as string;
        }
      }
      if (active) setInvoiceByBooking(map);
    })();
    return () => { active = false; };
  }, [bookings, selectedBusiness?.id]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isActionsOpen, setIsActionsOpen] = useState(false);
  const actionsRef = useRef<HTMLDivElement>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [bookingsToDelete, setBookingsToDelete] = useState<string[]>([]);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list'); // Default to list view
  
  // Removed debug logging - issue was identified in PrivateRoute and AppContent

  // Removed all persistence systems
  // No need for duplicate timer systems here



  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (actionsRef.current && !actionsRef.current.contains(event.target as Node)) {
        setIsActionsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset error state when business changes
  useEffect(() => {
    if (!selectedBusiness?.id) {
      setSearchQuery('');
      setSelectedBookings(new Set());
    }
  }, [selectedBusiness?.id]);

  // Handle navigation from customer details modal with booking ID
  useEffect(() => {
    const handleBookingNavigation = async () => {
      const state = location.state as { selectedBookingId?: string } | null;
      if (state?.selectedBookingId && selectedBusiness?.id) {
        try {
          // Fetch the specific booking details
          const booking = await getBooking(state.selectedBookingId);
          setSelectedBooking(booking);
          setIsBookingInfoModalOpen(true);
          
          // Clear the navigation state to prevent reopening on refresh
          // DISABLED - might be causing tab switch issues
          // window.history.replaceState({}, document.title);
        } catch (err) {
          console.error('Error fetching booking from navigation:', err);
        }
      }
    };

    handleBookingNavigation();
  }, [location.state, selectedBusiness?.id]);

  // Listen for Nexus booking modification events
  useEffect(() => {
    const handleNexusBookingUpdate = (event: any) => {
      const { businessId, modifications } = event.detail;
      console.log('🤖 Received Nexus booking update event in Bookings page:', { businessId, modifications });
      
      // Only refresh if the booking modification is for the current business
      if (businessId === selectedBusiness?.id) {
        console.log('🤖 Refreshing bookings list immediately for Nexus booking modifications');
        refetch(); // Refresh the bookings list
      }
    };

    // Add event listener for Nexus booking updates
    window.addEventListener('nexusBookingUpdate', handleNexusBookingUpdate);

    // Cleanup event listener on unmount
    return () => {
      window.removeEventListener('nexusBookingUpdate', handleNexusBookingUpdate);
    };
  }, [selectedBusiness?.id, refetch]);

  const filteredBookings = bookings.filter((booking) => {
    if (
      selectedStatus.value !== 'all' &&
      booking.status !== selectedStatus.value
    ) {
      return false;
    }

    const searchLower = searchQuery.toLowerCase();
    return (
      // Customer information
      booking.customer?.first_name?.toLowerCase().includes(searchLower) ||
      booking.customer?.last_name?.toLowerCase().includes(searchLower) ||
      booking.customer?.email?.toLowerCase().includes(searchLower) ||
      booking.customer?.phone?.toLowerCase().includes(searchLower) ||
      // Booking number (convert to string first)
      booking.booking_number?.toString().toLowerCase().includes(searchLower) ||
      // Invoice number (new invoicing system)
      invoiceByBooking[booking.id]?.toLowerCase().includes(searchLower) ||
      // Services and vehicle information
      (booking.services || []).some(
        (service) =>
          service.name.toLowerCase().includes(searchLower) ||
          service.vehicle_make?.toLowerCase().includes(searchLower) ||
          service.vehicle_model?.toLowerCase().includes(searchLower)
      )
    );
  });

  // Embedded view: in-progress jobs (any month) pinned on top, then the
  // locked month's remaining bookings.
  const displayBookings = useMemo(() => {
    if (!embeddedMonth) return filteredBookings;
    const pinned = inProgressBookings;
    const pinnedIds = new Set(pinned.map((b) => b.id));
    const rest = filteredBookings
      .filter((b) => !pinnedIds.has(b.id) && b.status !== 'in_progress')
      .sort((a, b) => {
        // Latest drop-off first, oldest at the bottom.
        const aKey = `${a.drop_off_date || a.start_date || '0000-01-01'} ${a.drop_off_time || a.start_time || '00:00'}`;
        const bKey = `${b.drop_off_date || b.start_date || '0000-01-01'} ${b.drop_off_time || b.start_time || '00:00'}`;
        return bKey.localeCompare(aKey);
      });
    return [...pinned, ...rest];
  }, [embeddedMonth, filteredBookings, inProgressBookings]);
  const pinnedCount = embeddedMonth ? inProgressBookings.length : 0;
  const monthLabel = embeddedMonth
    ? embeddedMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : '';

  // Section headers injected into the embedded list before the given index.
  const renderSectionHeader = (index: number, inGrid = false) => {
    if (!embeddedMonth || pinnedCount === 0) return null;
    if (index === 0) {
      return (
        <div className={`flex items-center gap-2 pt-1 ${inGrid ? 'col-span-full -mb-3' : 'pb-1'}`}>
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500" />
          </span>
          <span className="text-[13px] font-semibold text-gray-900">In Progress</span>
          <span className="text-[13px] text-gray-400">{pinnedCount}</span>
        </div>
      );
    }
    if (index === pinnedCount) {
      return (
        <div
          className={`flex items-center gap-2 pt-4 border-t border-gray-200/70 ${
            inGrid ? 'col-span-full -mb-3 mt-1' : 'pb-1 mt-2'
          }`}
        >
          <span className="text-[13px] font-semibold text-gray-900">{monthLabel}</span>
        </div>
      );
    }
    return null;
  };

  const toggleAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedBookings(new Set(bookings.map((booking) => booking.id)));
    } else {
      setSelectedBookings(new Set());
    }
  };

  const toggleBooking = (id: string) => {
    const newSelected = new Set(selectedBookings);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedBookings(newSelected);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'text-white';
      case 'scheduled':
        return 'text-white';
      case 'confirmed':
        return 'text-white';
      case 'in_progress':
        return 'text-white';
      case 'completed':
        return 'text-white';
      case 'cancelled':
        return 'text-red-700';
      default:
        return 'text-white';
    }
  };

  const getStatusBackgroundColor = (status: string) => {
    switch (status) {
      case 'pending':
        return '#a855f7'; // Purple
      case 'scheduled':
        return '#3b82f6'; // Blue
      case 'confirmed':
        return '#3b82f6'; // Blue
      case 'in_progress':
        return '#eab308'; // Yellow
      case 'completed':
        return '#22c55e'; // Green
      case 'cancelled':
        return '#fee2e2'; // Pale red (red-100)
      default:
        return '#3b82f6'; // Default blue
    }
  };


  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-gray-50 to-gray-100" style={{ 
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)'
      }}>
        <div className="bg-white/80 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-gray-200/50 text-center max-w-md mx-4" style={{
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)'
        }}>
          <div className="text-red-600 text-lg font-semibold mb-4">Error: {error}</div>
          <button
            onClick={() => refetch()}
            className="bg-blue-700 hover:bg-blue-600 text-white font-semibold py-3 px-6 rounded-2xl transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-sm touch-manipulation"
            style={{ minHeight: '44px' }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!selectedBusiness) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-gray-50 to-gray-100" style={{ 
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)'
      }}>
        <div className="bg-white/80 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-gray-200/50 text-center max-w-md mx-4" style={{
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)'
        }}>
          <div className="text-gray-700 text-lg font-medium">Please select a business to view bookings.</div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="flex flex-col h-full overflow-y-auto bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen" 
      data-bookings-page
      style={{ 
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        margin: 0,
        padding: 0
      }}>

      {/* Filters and Search Section */}
      <div className="bg-white/90 backdrop-blur-xl border-b border-gray-200/30 px-4 md:px-8 py-4 sticky top-0 z-30" style={{
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)'
      }}>
        <div className="flex flex-col space-y-4 md:space-y-0 md:flex-row md:justify-between md:items-center">
          {/* Filters Row */}
          <div className="flex flex-row items-center gap-3">
            {/* Status + date filters are hidden when embedded in the calendar
                (list is locked to the calendar's month there) */}
            {!embeddedMonth && (
            <>
            {/* Status Filter */}
            <div className="relative flex-1 sm:flex-none sm:w-auto">
              <button
                onClick={() => setIsStatusOpen(!isStatusOpen)}
                className="w-full sm:w-auto inline-flex items-center justify-between px-4 py-3 text-sm font-semibold text-gray-900 bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl hover:bg-white/90 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-sm touch-manipulation"
                style={{ minHeight: '44px' }}
              >
                <span>{selectedStatus.label}</span>
                <ChevronDown
                  className={`ml-2 h-5 w-5 transition-transform duration-300 ${
                    isStatusOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {isStatusOpen && (
                <div className="absolute left-0 mt-2 w-full sm:w-56 rounded-2xl shadow-2xl bg-white/95 backdrop-blur-xl border border-gray-200/50 z-50 transform transition-all duration-300 ease-out animate-in slide-in-from-top-2 fade-in-0" style={{
                  backdropFilter: 'blur(20px) saturate(180%)',
                  WebkitBackdropFilter: 'blur(20px) saturate(180%)'
                }}>
                  <div className="py-2" role="menu" aria-orientation="vertical">
                    {statusOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => {
                          setSelectedStatus(option);
                          setIsStatusOpen(false);
                        }}
                        className={`w-full text-left px-4 py-3 text-sm transition-all duration-200 ease-out touch-manipulation ${
                          selectedStatus.value === option.value
                            ? 'bg-blue-50 text-blue-900 font-semibold'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                        role="menuitem"
                        style={{ minHeight: '44px' }}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Date Filter */}
            <div className="relative flex-1 sm:flex-none sm:w-auto">
              <button
                onClick={() => setIsDateFilterOpen(!isDateFilterOpen)}
                className="w-full sm:w-auto inline-flex items-center justify-between px-4 py-3 text-sm font-semibold text-gray-900 bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl hover:bg-white/90 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-sm touch-manipulation"
                style={{ minHeight: '44px' }}
              >
                <span>{selectedDateFilter.label}</span>
                <ChevronDown
                  className={`ml-2 h-5 w-5 transition-transform duration-300 ${
                    isDateFilterOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {isDateFilterOpen && (
                <div className="absolute left-0 mt-2 w-full sm:w-56 rounded-2xl shadow-2xl bg-white/95 backdrop-blur-xl border border-gray-200/50 z-50 transform transition-all duration-300 ease-out animate-in slide-in-from-top-2 fade-in-0" style={{
                  backdropFilter: 'blur(20px) saturate(180%)',
                  WebkitBackdropFilter: 'blur(20px) saturate(180%)'
                }}>
                  <div className="py-2" role="menu" aria-orientation="vertical">
                    {dateFilterOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => {
                          setSelectedDateFilter(option);
                          setIsDateFilterOpen(false);
                        }}
                        className={`w-full text-left px-4 py-3 text-sm transition-all duration-200 ease-out touch-manipulation ${
                          selectedDateFilter.value === option.value
                            ? 'bg-blue-50 text-blue-900 font-semibold'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                        role="menuitem"
                        style={{ minHeight: '44px' }}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            </>
            )}

            {/* Bulk Actions */}
            {selectedBookings.size > 0 && (
              <div className="relative w-full sm:w-auto" ref={actionsRef}>
                <button
                  onClick={() => setIsActionsOpen(!isActionsOpen)}
                  className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-3 text-sm font-semibold text-white bg-blue-700 hover:bg-blue-600 border border-blue-700 rounded-2xl transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-sm touch-manipulation"
                  style={{ minHeight: '44px' }}
                >
                  <span>Actions ({selectedBookings.size})</span>
                  <ChevronDown className={`ml-2 h-4 w-4 transition-transform duration-300 ${isActionsOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isActionsOpen && (
                  <div className="absolute left-0 mt-2 w-full sm:w-56 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/50 py-2 z-50 transform transition-all duration-300 ease-out animate-in slide-in-from-top-2 fade-in-0" style={{
                    backdropFilter: 'blur(20px) saturate(180%)',
                    WebkitBackdropFilter: 'blur(20px) saturate(180%)'
                  }}>
                    <button
                      onClick={async () => {
                        setBookingsToDelete(Array.from(selectedBookings));
                        setIsDeleteModalOpen(true);
                        setIsActionsOpen(false);
                      }}
                      className="w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50 flex items-center disabled:opacity-50 transition-all duration-200 ease-out touch-manipulation"
                      disabled={isDeleting}
                      style={{ minHeight: '44px' }}
                    >
                      <Trash2 className="h-4 w-4 mr-3" />
                      {isDeleting ? 'Deleting...' : 'Delete Selected'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Search Bar and View Toggle */}
          <div className="flex items-center gap-3">
            {/* View Toggle - Desktop Only */}
            <div className="hidden sm:flex items-center bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl p-1 shadow-sm">
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-2 rounded-xl text-sm font-semibold transition-all duration-150 touch-manipulation ${
                  viewMode === 'list'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100 active:bg-gray-200'
                }`}
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <List className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`px-3 py-2 rounded-xl text-sm font-semibold transition-all duration-150 touch-manipulation ${
                  viewMode === 'grid'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100 active:bg-gray-200'
                }`}
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <Grid className="h-4 w-4" />
              </button>
            </div>

            {/* Search Bar — hidden when embedded (calendar already has search) */}
            {!embeddedMonth && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search bookings"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64"
              />
            </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Container */}
      <div className="flex-1 px-4 md:px-8 py-6">
        {/* Mobile Booking List */}
        <div className="sm:hidden">
          {loading ? (
            <BookingSkeletonList count={8} isMobile={true} />
          ) : displayBookings.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <EmptyState
                icon={<Calendar className="w-8 h-8 text-gray-400" />}
                title={searchQuery ? 'No matching bookings' : 'No bookings yet'}
                description={
                  searchQuery
                    ? 'Try adjusting your search terms or filters'
                    : 'Start adding bookings to manage your schedule and track services.'
                }
                actionLabel={
                  searchQuery ? 'Clear search' : 'Create Booking'
                }
                onAction={() => (searchQuery ? setSearchQuery('') : setIsCreateBookingModalOpen(true))}
              />
            </div>
          ) : (
            <div className="space-y-4">
              {displayBookings.map((booking, index) => (
                <React.Fragment key={booking.id}>
                {renderSectionHeader(index)}
                <div
                  className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300 ease-out transform hover:scale-[1.02] active:scale-[0.98] cursor-pointer flex"
                  onClick={(e) => {
                    if (permissionLevel === 'manage' || permissionLevel === 'view') {
                      setSelectedBooking(booking);
                      setIsBookingInfoModalOpen(true);
                    }
                  }}
                  style={{
                    animationDelay: `${index * 50}ms`
                  }}
                >
                  {/* Status Bar - Left Edge */}
                  <div className={`w-1 rounded-l-xl ${
                    booking.status === 'completed' ? 'bg-green-500' :
                    booking.status === 'in_progress' ? 'bg-yellow-500' :
                    booking.status === 'scheduled' ? 'bg-blue-500' :
                    booking.status === 'cancelled' ? 'bg-red-500' :
                    'bg-gray-400'
                  }`} />
                  
                  <div className="p-4 flex-1">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center text-xs font-bold text-white">
                          {booking.customer?.first_name?.[0]}{booking.customer?.last_name?.[0]}
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-gray-900 truncate">
                            {booking.customer?.first_name} {booking.customer?.last_name}
                          </h3>
                          <p className="text-xs text-gray-500">#{booking.booking_number}{invoiceByBooking[booking.id] ? ` · Inv ${invoiceByBooking[booking.id]}` : ''}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          booking.status === 'completed' ? 'bg-green-100 text-green-700' :
                          booking.status === 'in_progress' ? 'bg-yellow-100 text-yellow-700' :
                          booking.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
                          booking.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {booking.status.charAt(0).toUpperCase() + booking.status.slice(1).replace('_', ' ')}
                        </span>
                        {booking.start_date && (
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(booking.start_date).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Services */}
                    <div className="mb-3">
                      {booking.services?.slice(0, 2).map((service, index) => (
                        <div key={index} className="text-sm text-gray-700 mb-1 last:mb-0">
                          <span className="font-medium">{service.name}</span>
                          <span className="text-gray-500 ml-1 text-xs">
                            • {service.vehicle_make} {service.vehicle_model}
                          </span>
                        </div>
                      ))}
                      {(booking.services?.length || 0) > 2 && (
                        <div className="text-xs text-gray-500 font-medium">
                          +{(booking.services?.length || 0) - 2} more services
                        </div>
                      )}
                    </div>

                    {/* Amount and Date */}
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xl font-bold text-gray-900">
                          ${booking.total_amount?.toLocaleString() || '0.00'}
                        </p>
                      </div>
                      <div className="text-right">
                        {booking.start_date && (
                          <>
                            <p className="text-xs text-gray-500">
                              Scheduled
                            </p>
                            <p className="text-sm font-medium text-gray-900">
                              {booking.start_time || 'Time TBD'}
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                </React.Fragment>
              ))}
            </div>
          )}
        </div>

        {/* Desktop View (Grid or List) */}
        <div className="hidden sm:block">
        {error && (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="bg-red-50/80 backdrop-blur-sm border border-red-200/50 text-red-600 px-6 py-4 rounded-2xl text-sm max-w-md mb-4" style={{
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)'
              }}>
                {error}
              </div>
              {deleteError && (
                <div className="bg-red-50/80 backdrop-blur-sm border border-red-200/50 text-red-600 px-6 py-4 rounded-2xl text-sm max-w-md mt-4" style={{
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)'
                }}>
                  {deleteError}
                </div>
              )}
              <button
                onClick={() => refetch()}
                className="mt-6 bg-purple-700 hover:bg-purple-600 text-white font-semibold py-3 px-6 rounded-2xl transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-sm touch-manipulation"
                style={{ minHeight: '44px' }}
              >
                Try again
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <BookingSkeletonList count={10} isMobile={false} />
        ) : displayBookings.length === 0 ? (
          <div className="flex items-center justify-center h-64 bg-white/90 backdrop-blur-xl rounded-2xl border border-gray-200/50 shadow-lg" style={{
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)'
          }}>
            <EmptyState
              icon={<Calendar className="w-8 h-8 text-gray-400" />}
              title={searchQuery ? 'No matching bookings' : 'No bookings yet'}
              description={
                searchQuery
                  ? 'Try adjusting your search terms or date filters'
                  : 'Start creating bookings to manage your appointments.'
              }
              actionLabel={
                searchQuery ? 'Clear search' : 'Create Booking'
              }
              onAction={() => (searchQuery ? setSearchQuery('') : setIsCreateBookingModalOpen(true))}
            />
          </div>
        ) : viewMode === 'list' ? (
          /* List View */
          <div className="space-y-3">
            {displayBookings.map((booking, index) => (
              <React.Fragment key={booking.id}>
              {renderSectionHeader(index)}
              <div
                className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-200 cursor-pointer flex"
                onClick={() => {
                  if (permissionLevel === 'manage' || permissionLevel === 'view') {
                    setSelectedBooking(booking);
                    setIsBookingInfoModalOpen(true);
                  }
                }}
                style={{
                  animationDelay: `${index * 30}ms`
                }}
              >
                {/* Status Bar - Left Edge */}
                <div className={`w-1 rounded-l-xl ${
                  booking.status === 'completed' ? 'bg-green-500' :
                  booking.status === 'in_progress' ? 'bg-yellow-500' :
                  booking.status === 'scheduled' ? 'bg-blue-500' :
                  booking.status === 'cancelled' ? 'bg-red-500' :
                  'bg-gray-400'
                }`} />
                
                <div className="p-4 flex-1 flex items-center justify-between">
                  {/* Left Section - Customer Info */}
                  <div className="flex items-center space-x-4 flex-1 min-w-0">
                    <div className="h-12 w-12 rounded-full bg-blue-500 flex items-center justify-center text-sm font-bold text-white flex-shrink-0">
                      {booking.customer?.first_name?.[0]}{booking.customer?.last_name?.[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3 mb-1">
                        <h3 className="text-base font-semibold text-gray-900 truncate">
                          {booking.customer?.first_name} {booking.customer?.last_name}
                        </h3>
                        <span className="text-xs text-gray-500">#{booking.booking_number}{invoiceByBooking[booking.id] ? ` · Inv ${invoiceByBooking[booking.id]}` : ''}</span>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          booking.status === 'completed' ? 'bg-green-100 text-green-700' :
                          booking.status === 'in_progress' ? 'bg-yellow-100 text-yellow-700' :
                          booking.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
                          booking.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {booking.status.charAt(0).toUpperCase() + booking.status.slice(1).replace('_', ' ')}
                        </span>
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-gray-600">
                        {booking.services && booking.services.length > 0 && (
                          <div className="flex items-center space-x-2">
                            <span className="font-medium">{booking.services[0].name}</span>
                            {booking.services[0].vehicle_make && (
                              <span className="text-gray-500">
                                • {booking.services[0].vehicle_make} {booking.services[0].vehicle_model}
                              </span>
                            )}
                            {(booking.services?.length || 0) > 1 && (
                              <span className="text-gray-500">
                                +{(booking.services?.length || 0) - 1} more
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Right Section - Date, Time, Amount */}
                  <div className="flex items-center space-x-6 flex-shrink-0">
                    {booking.start_date && (
                      <div className="text-right">
                        <p className="text-xs text-gray-500">Date</p>
                        <p className="text-sm font-medium text-gray-900">
                          {new Date(booking.start_date).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </p>
                        {booking.start_time && (
                          <p className="text-xs text-gray-500 mt-1">{booking.start_time}</p>
                        )}
                      </div>
                    )}
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Total</p>
                      <p className="text-lg font-bold text-gray-900">
                        ${booking.total_amount?.toLocaleString() || '0.00'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              </React.Fragment>
            ))}
          </div>
        ) : (
          /* Grid View */
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {displayBookings.map((booking, index) => (
              <React.Fragment key={booking.id}>
              {renderSectionHeader(index, true)}
              <div
                className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300 ease-out transform hover:scale-[1.02] active:scale-[0.98] cursor-pointer flex"
                onClick={() => {
                  if (permissionLevel === 'manage' || permissionLevel === 'view') {
                    setSelectedBooking(booking);
                    setIsBookingInfoModalOpen(true);
                  }
                }}
                style={{
                  animationDelay: `${index * 50}ms`
                }}
              >
                {/* Status Bar - Left Edge */}
                <div className={`w-1 rounded-l-xl ${
                  booking.status === 'completed' ? 'bg-green-500' :
                  booking.status === 'in_progress' ? 'bg-yellow-500' :
                  booking.status === 'scheduled' ? 'bg-blue-500' :
                  booking.status === 'cancelled' ? 'bg-red-500' :
                  'bg-gray-400'
                }`} />
                
                <div className="p-6 flex-1">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center text-sm font-bold text-white">
                        {booking.customer?.first_name?.[0]}{booking.customer?.last_name?.[0]}
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900 truncate">
                          {booking.customer?.first_name} {booking.customer?.last_name}
                        </h3>
                        <p className="text-xs text-gray-500">#{booking.booking_number}{invoiceByBooking[booking.id] ? ` · Inv ${invoiceByBooking[booking.id]}` : ''}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        booking.status === 'completed' ? 'bg-green-100 text-green-700' :
                        booking.status === 'in_progress' ? 'bg-yellow-100 text-yellow-700' :
                        booking.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
                        booking.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {booking.status.charAt(0).toUpperCase() + booking.status.slice(1).replace('_', ' ')}
                      </span>
                      {booking.start_date && (
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(booking.start_date).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Services */}
                  <div className="mb-4">
                    {booking.services?.slice(0, 2).map((service, index) => (
                      <div key={index} className="text-sm text-gray-700 mb-1 last:mb-0">
                        <span className="font-medium">{service.name}</span>
                        <span className="text-gray-500 ml-1 text-xs">
                          • {service.vehicle_make} {service.vehicle_model}
                        </span>
                      </div>
                    ))}
                    {(booking.services?.length || 0) > 2 && (
                      <div className="text-xs text-gray-500 font-medium">
                        +{(booking.services?.length || 0) - 2} more services
                      </div>
                    )}
                  </div>

                  {/* Amount and Time */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-2xl font-bold text-gray-900">
                        ${booking.total_amount?.toLocaleString() || '0.00'}
                      </p>
                    </div>
                    <div className="text-right">
                      {booking.start_date && (
                        <>
                          <p className="text-xs text-gray-500">
                            Scheduled
                          </p>
                          <p className="text-sm font-medium text-gray-900">
                            {booking.start_time || 'Time TBD'}
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              </React.Fragment>
            ))}
          </div>
        )}
        </div>
      </div>

      <CreateBookingModal
        isOpen={isCreateBookingModalOpen}
        onClose={() => {
          // clearModalData(); // Clear modal persistence data - now handled by usePageLifecycle
          setIsCreateBookingModalOpen(false);
        }}
        onSuccess={() => {
          // clearModalData(); // Clear modal persistence data - now handled by usePageLifecycle
          setIsCreateBookingModalOpen(false);
          refetch();
        }}
      />

      <BookingInfoModal
        isOpen={isBookingInfoModalOpen}
        onClose={() => {
          setIsBookingInfoModalOpen(false);
          setSelectedBooking(null);
          refetch();
        }}
        booking={selectedBooking}
        onUpdate={refetch}
        readOnly={permissionLevel === 'view'}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setBookingsToDelete([]);
        }}
        onConfirm={async () => {
          try {
            setDeleteLoading(true);
            setDeleteError(null);
            
            // 🎯 For each booking to delete, cancel automations and trigger deletion automations BEFORE deleting
            if (selectedBusiness?.id) {
              for (const bookingId of bookingsToDelete) {
                try {
                  console.log(`[BULK DELETE] Processing booking ${bookingId}...`);
                  
                  // 🚫 Cancel existing automations for this booking
                  try {
                    console.log(`[BULK DELETE] 🚫 Cancelling existing automations for booking ${bookingId}`);
                    const cancellationResult = await cancelBookingAutomations(
                      bookingId,
                      'booking_deleted',
                      selectedBusiness.id
                    );
                    
                    if (cancellationResult.success) {
                      console.log(`[BULK DELETE] ✅ Successfully cancelled ${cancellationResult.cancelledJobs} automation jobs for booking ${bookingId}`);
                    } else {
                      console.warn(`[BULK DELETE] ⚠️ Automation cancellation had issues for booking ${bookingId}:`, cancellationResult.errors);
                    }
                  } catch (error) {
                    console.error(`[BULK DELETE] ❌ Error cancelling automations for booking ${bookingId}:`, error);
                    // Don't fail the deletion if automation cancellation fails
                  }
                  
                  // Find the full booking data from our current bookings list
                  const bookingToDelete = bookings.find(b => b.id === bookingId);
                  
                  if (bookingToDelete) {
                    console.log(`[BULK DELETE] 🚀 Triggering NEW QStash automations for deleted booking ${bookingToDelete.booking_number}`);
                    
                    // Prepare booking data for automation trigger
                    const realBookingData = {
                      booking_id: bookingToDelete.id,
                      booking_number: bookingToDelete.booking_number,
                      title: bookingToDelete.title,
                      customer: {
                        full_name: `${bookingToDelete.customer?.first_name || ''} ${bookingToDelete.customer?.last_name || ''}`.trim(),
                        phone: bookingToDelete.customer?.phone || undefined,
                        email: bookingToDelete.customer?.email || undefined
                      },
                      schedule: {
                        drop_off_date: bookingToDelete.drop_off_date || '',
                        drop_off_time: bookingToDelete.drop_off_time || '',
                        start_date: bookingToDelete.start_date || '',
                        start_time: bookingToDelete.start_time || '',
                        end_date: bookingToDelete.end_date || '',
                        end_time: bookingToDelete.end_time || ''
                      },
                      services: (bookingToDelete.services || []).map(service => ({
                        name: service.name,
                        price: service.price,
                        duration_minutes: service.duration_minutes || 60
                      })),
                      grand_total: (bookingToDelete as any).grand_total || bookingToDelete.subtotal || 0,
                      status: 'deleted',
                      resources: (bookingToDelete.resources || []).map(resource => ({
                        name: `${(resource as any).first_name || ''} ${(resource as any).last_name || ''}`.trim(),
                        email: (resource as any).email || '',
                        phone: (resource as any).phone || ''
                      })),
                      source: 'bulk_delete_action'
                    };

                    console.log(`🎯 BULK DELETE: Triggering QStash automations for booking ${bookingToDelete.booking_number}:`, realBookingData);
                    await triggerBookingAutomations(selectedBusiness.id, 'deleted', realBookingData);
                    console.log(`🎯 BULK DELETE: QStash automations triggered successfully for booking ${bookingToDelete.booking_number}`);
                  }
                } catch (automationError) {
                  console.error(`❌ BULK DELETE: Failed to trigger QStash automations for booking ${bookingId}:`, automationError);
                  // Don't fail the entire deletion process if automation fails for one booking
                }
              }
            }
            
            // Now delete all the bookings
            await Promise.all(
              bookingsToDelete.map(id => deleteBooking(id))
            );
            setSelectedBookings(new Set());
            await refetch();
            setIsDeleteModalOpen(false);
            setBookingsToDelete([]);
          } catch (err) {
            console.error('Error deleting bookings:', err);
            setDeleteError(err instanceof Error ? err.message : 'Failed to delete bookings');
          } finally {
            setDeleteLoading(false);
          }
        }}
        title={`Delete ${bookingsToDelete.length > 1 ? 'Bookings' : 'Booking'}`}
        message={`Are you sure you want to delete ${bookingsToDelete.length} booking${bookingsToDelete.length > 1 ? 's' : ''}? This action cannot be undone.`}
        loading={deleteLoading}
      />
    </div>
  );
}

// Wrap with PermissionGuard
function BookingsWithPermission() {
  return (
    <PermissionGuard permission="bookings.pageAccess">
      <Bookings />
    </PermissionGuard>
  );
}

export default BookingsWithPermission;